import { useRef, useState, useEffect } from "react";
import LoadingDesignFiles from "./components/LoadingDesignFiles";
import RegistrationTypeSwitch from "./components/RegistrationTypeSwitch";
import DesignUpload from "./components/DesignUpload";
import { scrollToCurrentStep } from "../../utils/scrollToCurrentStep";
import { validateEmail, websiteValidation } from "../../utils/validation";
import {
  uploadFiles,
  uploadFigmaUrl,
  handleUploadError,
} from "../../utils/uploadUtils";
import { validateWebsiteData, scrapeWebsite } from "../../utils/websiteUtils";
import WebsiteAnalysis from "./components/WebsiteAnalysis";
import Button from "../../components/UI/Button";

export function RegistrationStep({
  nextStep,
  userData,
  setUserData,
  choseType,
  handleTypeChange,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [figmaUrl, setFigmaUrl] = useState("");
  const [inputError, setInputError] = useState("");
  const [uploadForm, setUploadForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [fileUploadError, setFileUploadError] = useState([]);
  const [figmaUrlError, setFigmaUrlError] = useState([]);
  const sectionRef = useRef(null);
  const [userWebsiteData, setUserWebsiteData] = useState({
    name: "",
    email: "",
    website: "",
    phone: "",
  });
  const [validationWebsite, setValidationWebsite] = useState({
    name: false,
    email: false,
    website: false,
    phone: false,
  });
  const [scrapperError, setScrapperError] = useState("");

  useEffect(() => {
    if (files.length > 0 && figmaUrl) {
      setInputError(
        "Please upload either design files or a Figma URL, not both"
      );
      scrollToCurrentStep(sectionRef);
    } else {
      setInputError("");
    }
  }, [files, figmaUrl]);

  useEffect(() => {
    if (window.userProfile) {
      const { name, email, phone } = window.userProfile;
      setUploadForm((prevForm) => ({
        ...prevForm,
        name: name || "",
        email: email || "",
        phone: phone || "",
      }));
      setUserWebsiteData((prevData) => ({
        ...prevData,
        name: name || "",
        email: email || "",
        phone: phone || "",
      }));
    }
  }, []);

  const handleValidateWebsite = async () => {
    const isValid = validateWebsiteData(userWebsiteData);

    setValidationWebsite(isValid);

    if (!isValid.name && !isValid.email && !isValid.website) {
      scrollToCurrentStep(sectionRef);

      setIsLoading(true);

      try {
        const data = await scrapeWebsite(userWebsiteData);
        setUserData({
          ...userData,
          id: data.id,
          pages: data.transformedData,
          name: userWebsiteData.name,
          email: userWebsiteData.email,
          phone: userWebsiteData.phone,
        });

        nextStep();
      } catch (error) {
        setScrapperError(error.response.data);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDesignUpload = async () => {
    scrollToCurrentStep(sectionRef);

    setIsLoading(true);
    setFileUploadError([]);
    setFigmaUrlError([]);

    try {
      const data =
        files.length > 0
          ? await uploadFiles(files, uploadForm)
          : await uploadFigmaUrl(figmaUrl, uploadForm);

      setUserData((prevData) => ({
        ...prevData,
        id: data.id,
        name: uploadForm.name,
        email: uploadForm.email,
        phone: uploadForm.phone,
        AIResponse: [data.estimations],
        urls: data.urls,
        originalFiles: data.originalFiles,
      }));
      nextStep();
    } catch (error) {
      const errors = handleUploadError(error);
      files.length > 0 ? setFileUploadError(errors) : setFigmaUrlError(errors);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  return (
    <div className="app-ai-quote-registration" ref={sectionRef}>
      {isLoading ? (
        <LoadingDesignFiles files={files} />
      ) : (
        <>
          <RegistrationTypeSwitch
            choseType={choseType}
            handleTypeChange={handleTypeChange}
          />
          {choseType === "design" ? (
            <>
              <div className="app-ai-quote-design" data-aos="fade-up">
                <form
                  className="app-ai-quote-design-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="name">
                      Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={uploadForm.name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="email">
                      Email <span>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      aria-labelledby="email-control-label"
                      aria-describedby="email-help-text"
                      value={uploadForm.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="app-ai-quote-form-field">
                    <label htmlFor="phone">
                      Phone <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={uploadForm.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </form>
                <DesignUpload
                  files={files}
                  setFiles={setFiles}
                  formUpload={handleDesignUpload}
                  setFigmaUrl={setFigmaUrl}
                  figmaUrl={figmaUrl}
                  fileUploadError={fileUploadError}
                  figmaUrlError={figmaUrlError}
                />
                {inputError && (
                  <div className="app-ai-quote-error-message">
                    <p>{inputError}</p>
                  </div>
                )}
                <p>We will send the final quote to your email address.</p>
                <div className="app-ai-quote-button-group">
                  <Button
                    type="submit"
                    onClick={handleDesignUpload}
                    disabled={
                      uploadForm.name.length < 2 ||
                      !validateEmail(uploadForm.email) ||
                      (!files.length && !websiteValidation(figmaUrl)) ||
                      inputError !== ""
                    }
                  >
                    Calculate Now
                  </Button>
                  <p>
                    Pressing this button indicates your consent
                    <br /> to our{" "}
                    <a href="https://getdevdone.com/terms-and-conditions.html">
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href="https://getdevdone.com/privacy-policy.html">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </>
          ) : (
            <WebsiteAnalysis
              userData={userWebsiteData}
              formUpload={handleValidateWebsite}
              setUserWebsiteData={setUserWebsiteData}
              validationWebsite={validationWebsite}
              scrapperError={scrapperError}
            />
          )}
        </>
      )}
    </div>
  );
}
